import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { 
    FaUserMd, 
    FaCalendarAlt, 
    FaUserInjured, 
    FaStethoscope, 
    FaMicroscope,
    FaFlask, 
    FaCog,
    FaBars,
    FaTimes,
    FaSignOutAlt,
    FaHome,
    FaChartLine,
    FaUser
} from 'react-icons/fa';
import logo from './Designer.png';

const DoctorNavbar = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    
    // Get authentication context
    const { currentUser, logout } = useAuth();

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 10) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Handle logout function
    const handleLogout = () => {
        logout();
        navigate('/login');
        setIsMobileMenuOpen(false);
    };

    // Get user's full name or default
    const userName = currentUser ? `Dr. ${currentUser.firstName} ${currentUser.lastName}` : 'Doctor';
    
    // Dynamic nav items based on current user
    const navItems = [
        { name: 'Dashboard', path: '/dashboard', icon: <FaHome /> },
        // { name: 'Appointments', path: '/appointments', icon: <FaCalendarAlt /> },
        { name: 'Patients', path: '/patients', icon: <FaUserInjured /> },
        // { name: 'Consultations', path: '/consultations', icon: <FaStethoscope /> },
        // { name: 'Test Results', path: '/test-results', icon: <FaFlask /> },
        // { name: 'Analytics', path: '/analytics', icon: <FaChartLine /> },
        // { name: 'Profile', path: '/profile', icon: <FaUser /> },
    ];

    const isActive = (path) => {
        return location.pathname === path;
    };

    return (
        <>
            <nav className={`fixed w-full text-white top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-blue-900/95 shadow-lg backdrop-blur-sm py-2' : 'bg-blue-900 py-4'}`}>
                <div className="container mx-auto flex justify-between items-center px-4">
                    <Link to="/" className="text-2xl font-bold flex items-center group">
                        <img 
                            src={logo} 
                            alt="SeraphMed Logo" 
                            className="h-9 mr-2 transform group-hover:scale-105 transition-transform duration-300" 
                        />
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200">
                            SeraphMed
                        </span>
                    </Link>
                    
                    {/* Desktop menu */}
                    <div className="hidden md:flex items-center">
                        <ul className="flex items-center space-x-2 lg:space-x-4 mr-6">
                            {navItems.map((item) => (
                                <li key={item.path}>
                                    <Link
                                        to={item.path}
                                        className={`flex items-center px-3 py-2 rounded-md transition-all duration-200 ${
                                            isActive(item.path) ? 'bg-blue-700 text-white shadow-md' : 'hover:bg-blue-800/70 hover:text-white'
                                        }`}
                                    >
                                        <span className="mr-2">{item.icon}</span>
                                        {item.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                        
                        {/* User profile & logout */}
                        <div className="flex items-center border-l border-blue-700/50 pl-6">
                            <div className="mr-4">
                                <span className="text-sm opacity-75">Welcome,</span>
                                <h3 className="font-medium text-white">{userName}</h3>
                            </div>
                            <button 
                                onClick={handleLogout}
                                className="px-4 py-2 border border-blue-300/70 rounded-md hover:bg-blue-800 hover:border-blue-200 transition-all duration-200 hover:shadow-md flex items-center"
                            >
                                <FaSignOutAlt className="mr-2" />
                                <span className="font-medium">Logout</span>
                            </button>
                        </div>
                    </div>
                    
                    {/* Mobile menu toggle */}
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="md:hidden focus:outline-none flex flex-col items-center justify-center"
                        aria-label="Toggle menu"
                    >
                        <span className={`block w-6 h-0.5 bg-white mb-1.5 transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                        <span className={`block w-6 h-0.5 bg-white mb-1.5 transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                        <span className={`block w-6 h-0.5 bg-white transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                    </button>
                </div>
                
                {/* Mobile menu */}
                <div className={`transform transition-all duration-300 ease-in-out md:hidden overflow-hidden ${isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
                    <ul className="bg-blue-800 shadow-inner px-4 py-2">
                        {/* User profile section for mobile */}
                        <div className="border-b border-blue-700 mb-3 pb-3">
                            <div className="flex items-center">
                                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center mr-3 shadow-md">
                                    {currentUser && (
                                        <span className="text-white font-medium">
                                            {currentUser.firstName?.charAt(0)}{currentUser.lastName?.charAt(0)}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <h3 className="font-medium text-white">{userName}</h3>
                                    <p className="text-xs text-blue-200">Medical Doctor</p>
                                </div>
                            </div>
                        </div>
                        
                        {/* Navigation items */}
                        {navItems.map((item) => (
                            <li key={item.path}>
                                <Link
                                    to={item.path}
                                    className={`flex items-center py-3 border-b border-blue-700 hover:text-blue-200 transition-colors ${isActive(item.path) ? 'text-white font-medium' : ''}`}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    <span className="w-8 h-8 flex items-center justify-center mr-3">{item.icon}</span>
                                    {item.name}
                                </Link>
                            </li>
                        ))}
                        
                        {/* Logout button */}
                        <li>
                            <button
                                onClick={handleLogout}
                                className="flex items-center py-3 w-full text-left hover:text-blue-200 transition-colors"
                            >
                                <span className="w-8 h-8 flex items-center justify-center mr-3">
                                    <FaSignOutAlt />
                                </span>
                                Logout
                            </button>
                        </li>
                    </ul>
                </div>
            </nav>
        </>
    );
};

export default DoctorNavbar;